import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { ToastNotification, useToastNotifications } from "@/components/ui/use-toast"
import { classes } from "@/utils"
import { formatDistanceToNow } from "date-fns"
import { BellOff } from "lucide-react"
import { useEffect } from "preact/hooks"

interface NotificationsSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Left-edge accent so a failure stands out from the run of successes. */
function notification_accent_class(notification: ToastNotification): string {
  return notification.variant === "destructive" ? "border-l-red-500" : "border-l-emerald-500"
}

function NotificationRow({ notification }: { notification: ToastNotification }) {
  return (
    <li
      class={classes(
        "border-l-4 rounded-r-md bg-gray-50 px-3 py-2 flex flex-col gap-1",
        notification_accent_class(notification),
        notification.read ? "opacity-80" : ""
      )}>
      <div class="flex items-start justify-between gap-3">
        {notification.title ? <div class="text-sm font-semibold leading-tight">{notification.title}</div> : null}
        <span class="text-[11px] text-gray-500 whitespace-nowrap shrink-0" title={notification.created_at.toLocaleString()}>
          {formatDistanceToNow(notification.created_at, { addSuffix: true })}
        </span>
      </div>
      {notification.description ? (
        <div class="text-sm text-gray-700 [&_a]:underline">{notification.description}</div>
      ) : null}
    </li>
  )
}

/**
 * Every toast shown this session, newest first. Toasts show one at a time and vanish after a few
 * seconds, so anything that fires several at once (a bulk CodingWiz run, a batch of status
 * updates) is unreadable on screen; this is where the full list can be read back.
 */
export function NotificationsSheet({ open, onOpenChange }: NotificationsSheetProps) {
  const { notifications, mark_all_read, clear } = useToastNotifications()

  // Opening the list is what counts as reading it.
  useEffect(() => {
    if (open) {
      mark_all_read()
    }
  }, [open, notifications.length])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent class="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Notifications</SheetTitle>
          <SheetDescription>Recent messages from this session, newest first.</SheetDescription>
        </SheetHeader>
        <div class="mt-4 flex flex-col gap-3">
          {notifications.length === 0 ? (
            <div class="flex flex-col items-center gap-2 py-12 text-gray-500">
              <BellOff className="h-6 w-6" />
              <span class="text-sm">Nothing yet.</span>
            </div>
          ) : (
            <>
              <div class="flex justify-end">
                <Button variant="ghost" size="sm" onClick={clear}>
                  Clear all
                </Button>
              </div>
              <ul class="flex flex-col gap-2">
                {notifications.map((notification: ToastNotification) => (
                  <NotificationRow key={notification.id} notification={notification} />
                ))}
              </ul>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
